import { addDays, differenceInCalendarDays, eachDayOfInterval, endOfMonth, endOfWeek, getISOWeek, parseISO, startOfMonth, startOfWeek } from "date-fns";
import { toISODate } from "@/lib/format";
import { occurrencesBetween, weekdayIndex } from "@/lib/recurring";

export { weekdayIndex };

export const WHOLE = "heile-huset";
/** The item currently being dragged (HTML5 DnD hides dataTransfer during dragover). */
export const dragStore = { current: null };
export const ROOM_COLOURS = 8;

/** Stable colour index per room: by display order, «heile huset» always ink (8). */
export const roomColourMap = (rooms = []) => {
  const map = {};
  const sorted = [...rooms].filter((r) => r.id !== WHOLE).sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  sorted.forEach((r, i) => { map[r.id] = (i % (ROOM_COLOURS - 1)) + 1; });
  map[WHOLE] = ROOM_COLOURS;
  return map;
};

/** CSS custom property for an item's hue – first room decides, heile huset wins. */
export const colourVar = (roomIds = [], map = {}) => {
  const idx = roomIds.includes(WHOLE) ? ROOM_COLOURS : map[roomIds[0]] || 7;
  return `var(--room-${idx})`;
};

export const toMin = (t) => {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
};
export const minToTime = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

export const weekOf = (d) => startOfWeek(d, { weekStartsOn: 1 });
export const weekDays = (d) => eachDayOfInterval({ start: weekOf(d), end: endOfWeek(d, { weekStartsOn: 1 }) });
export const isoWeek = (d) => getISOWeek(d);

/** The 5–6 weeks that cover a month, each as 7 Dates (Monday first). */
export const weeksOfMonth = (month) => {
  const first = weekOf(startOfMonth(month));
  const last = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const weeks = [];
  let cur = first;
  while (cur <= last) {
    weeks.push(eachDayOfInterval({ start: cur, end: addDays(cur, 6) }));
    cur = addDays(cur, 7);
  }
  return weeks;
};

/* ------------------------------------------------------------------ */
/* Items: one shape for calendar entries, pending bookings and recurring */
/* ------------------------------------------------------------------ */

export const entryItem = (e) => ({
  key: `e-${e.id}`, kind: "entry", id: e.id, title: e.title, category: e.category, visibility: e.visibility,
  start_date: e.start_date, end_date: e.end_date || e.start_date, start_time: e.start_time || null, end_time: e.end_time || null,
  room_ids: e.room_ids || [], room_names: e.room_names || [], source: e.source, contact: e.contact_name, raw: e,
});

export const pendingItem = (b) => ({
  key: `b-${b.id}`, kind: "pending", id: b.id, title: b.event_title || b.event_type || "Førespurnad", category: b.event_type, visibility: b.purpose === "public_event" ? "public" : "private",
  start_date: b.start_date, end_date: b.end_date || b.start_date, start_time: b.start_time || null, end_time: b.end_time || null,
  room_ids: b.room_ids || [], room_names: b.room_names || [], contact: b.name, has_conflict: b.has_conflict, raw: b,
});

/** Expand weekly practices into dated items between `from` and `to` (inclusive ISO dates), honouring per-date exceptions.
 *  `roomNames` (id → name) resolves the room of occurrences moved to another room than the series. */
export const recurringItems = (recurring = [], from, to, roomNames = {}) =>
  occurrencesBetween(recurring, from, to).map(({ practice: r, ...occ }) => ({
    key: `r-${r.id}-${occ.occurrence_date}`, kind: "recurring", id: r.id, series_id: r.id, occurrence_date: occ.occurrence_date, overridden: occ.overridden,
    title: occ.title, category: r.group_name, start_date: occ.date, end_date: occ.date, start_time: occ.start_time, end_time: occ.end_time,
    room_ids: [occ.room_id], room_names: [roomNames[occ.room_id] || (occ.room_id === r.room_id ? r.room_name : null)].filter(Boolean), raw: r,
  }));

export const overlapsRange = (item, fromISO, toISO) => item.start_date <= toISO && (item.end_date || item.start_date) >= fromISO;
export const onDay = (item, iso) => item.start_date <= iso && (item.end_date || item.start_date) >= iso;
export const isMultiDay = (item) => (item.end_date || item.start_date) !== item.start_date;

const sortItems = (a, b) => {
  const order = { entry: 0, pending: 1, recurring: 2 };
  if (a.start_date !== b.start_date) return a.start_date < b.start_date ? -1 : 1;
  const am = isMultiDay(a) ? 0 : 1, bm = isMultiDay(b) ? 0 : 1;
  if (am !== bm) return am - bm;
  const at = toMin(a.start_time) ?? -1, bt = toMin(b.start_time) ?? -1;
  if (at !== bt) return at - bt;
  return (order[a.kind] ?? 9) - (order[b.kind] ?? 9);
};
export const sortByStart = (items) => [...items].sort(sortItems);

/**
 * Lay a week's items out as horizontal segments with lanes (no two overlapping segments share a lane).
 * Returns [{ item, start (0-6), end (0-6), lane, before, after }].
 */
export const weekSegments = (items, days) => {
  const first = days[0], last = days[6];
  const fromISO = toISODate(first), toISO = toISODate(last);
  const segs = [];
  for (const it of items) {
    if (!overlapsRange(it, fromISO, toISO)) continue;
    const s = parseISO(it.start_date), e = parseISO(it.end_date || it.start_date);
    const start = Math.max(0, differenceInCalendarDays(s, first));
    const end = Math.min(6, differenceInCalendarDays(e, first));
    segs.push({ item: it, start, end, before: s < first, after: e > last, lane: 0 });
  }
  segs.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start) || sortItems(a.item, b.item));
  const lanes = [];
  for (const seg of segs) {
    let lane = 0;
    while (lanes[lane] !== undefined && lanes[lane] >= seg.start) lane += 1;
    seg.lane = lane;
    lanes[lane] = seg.end;
  }
  return segs;
};

/**
 * Pack a day's timed items into side-by-side columns (week view). Returns [{ item, col, cols, top, height }] in minutes.
 */
export const dayColumns = (items, dayStartMin = 7 * 60, dayEndMin = 24 * 60) => {
  const timed = items.filter((i) => toMin(i.start_time) !== null).map((i) => {
    const s = Math.max(dayStartMin, toMin(i.start_time));
    const e = Math.min(dayEndMin, toMin(i.end_time) ?? Math.min(dayEndMin, s + 120));
    return { item: i, s, e: Math.max(e, s + 30), col: 0, cols: 1 };
  }).sort((a, b) => a.s - b.s || b.e - a.e);
  // cluster overlapping blocks, then assign columns within each cluster
  let cluster = [], clusterEnd = -1;
  const flush = () => {
    const colEnds = [];
    for (const b of cluster) {
      let c = 0;
      while (colEnds[c] !== undefined && colEnds[c] > b.s) c += 1;
      b.col = c; colEnds[c] = b.e;
    }
    for (const b of cluster) b.cols = colEnds.length;
    cluster = [];
  };
  for (const b of timed) {
    if (cluster.length && b.s >= clusterEnd) flush();
    cluster.push(b);
    clusterEnd = Math.max(clusterEnd, b.e);
  }
  if (cluster.length) flush();
  return timed;
};

export const fmtClock = (t) => (t ? t.replace(/^0/, "") : "");
export const timeLabel = (item) => (item.start_time ? `${fmtClock(item.start_time)}${item.end_time ? `–${fmtClock(item.end_time)}` : ""}` : "");
